import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  X, Plus, Loader2, AlertCircle, ChevronLeft, ChevronRight, Maximize2, Minimize2,
} from 'lucide-react';
import clsx from 'clsx';
import { PageHeader } from '@/components/PageHeader';
import { PainelEventoPanel } from '@/components/PainelEvento';
import { api, ApiError } from '@/lib/api';
import { buildFeriadoIndex } from '@/lib/feriados';
import type { Feriado } from '@/lib/feriados';
import type { Evento, FamiliaEvento, StatusEvento, TipoEvento } from '@/lib/types';

const FAMILIAS: FamiliaEvento[] = ['Conecta Folha Business', 'BRM - BUY', 'Setorial', 'Apex / Research'];
const STATUSES: StatusEvento[] = ['Planejado', 'Confirmado', 'Realizado', 'Cancelado'];
const TIPO_EVENTO_LABELS: Record<TipoEvento, string> = {
  proprietario: 'Proprietário',
  patrocinado: 'Patrocinado',
  setorial: 'Setorial',
  interno: 'Interno',
  institucional: 'Institucional',
  feriado: 'Feriado',
  comercial: 'Comercial',
  terceiros: 'Terceiros',
  mining_show: 'Mining Show',
  experiencia: 'Experiência',
};

const STATUS_COR: Record<StatusEvento, string> = {
  'Planejado':  'bg-blue/15 text-blue-light border-blue/30',
  'Confirmado': 'bg-green/15 text-green border-green/30',
  'Realizado':  'bg-muted/15 text-muted border-muted/30',
  'Cancelado':  'bg-red/15 text-red border-red/30',
};

const STATUS_PILL: Record<StatusEvento, string> = {
  'Planejado':  'bg-blue/80 text-white',
  'Confirmado': 'bg-green/80 text-white',
  'Realizado':  'bg-muted/60 text-white',
  'Cancelado':  'bg-red/70 text-white line-through',
};

const FAMILIA_COR: Record<FamiliaEvento, string> = {
  'Conecta Folha Business': 'border-blue/40',
  'BRM - BUY':              'border-purple/40',
  'Setorial':               'border-pink/40',
  'Apex / Research':        'border-green/40',
};

const DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MESES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

function fmt(iso?: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function parseISO(iso?: string): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

function sameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function eventoNoDia(ev: Evento, day: Date): boolean {
  const start = parseISO(ev.dataInicio);
  if (!start) return false;
  const end = parseISO(ev.dataFim) ?? start;
  // Zera horas pra comparar só data
  const s = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const e = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  const d = new Date(day.getFullYear(), day.getMonth(), day.getDate());
  return d >= s && d <= e;
}

export function Eventos() {
  const [eventoOpen, setEventoOpen] = useState<string | null>(null);
  const [novoOpen, setNovoOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [viewMode, setViewMode] = useState<'calendario' | 'execucao' | 'painel'>('calendario');
  const [painelEventoId, setPainelEventoId] = useState<string | null>(null);

  // Deep-link: /eventos?painel=<eventoId> abre direto o Painel do evento (widget da Home)
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const p = params.get('painel');
    if (p) {
      setPainelEventoId(p);
      setViewMode('painel');
      setParams({}, { replace: true });
    }
  }, [params, setParams]);
  const [feriadoDay, setFeriadoDay] = useState<Date | null>(null);
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [showFeriados, setShowFeriados] = useState(true);
  const [ufFiltro, setUfFiltro] = useState<'todos' | 'ES' | 'SC' | 'RS' | 'SP' | 'PR'>('todos');
  const [tipoEventoFiltro, setTipoEventoFiltro] = useState<TipoEvento | 'todos'>('todos');
  const [buscaEvento, setBuscaEvento] = useState('');
  const [statusEventoFiltro, setStatusEventoFiltro] = useState<StatusEvento | 'todos'>('todos');
  const [ufEventoFiltro, setUfEventoFiltro] = useState<string>('todos');
  const feriadoIdx = useMemo(
    () => buildFeriadoIndex([cursor.getFullYear() - 1, cursor.getFullYear(), cursor.getFullYear() + 1]),
    [cursor],
  );
  function feriadosNoDia(d: Date): Feriado[] {
    if (!showFeriados) return [];
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const list = feriadoIdx.get(iso) ?? [];
    if (ufFiltro === 'todos') return list;
    return list.filter((f) => f.tipo === 'nacional' || f.uf === ufFiltro);
  }

  const eventosQ = useQuery({ queryKey: ['eventos'], queryFn: () => api.eventos.list().then((r) => r.data) });
  const eventos = useMemo(() => {
    return (eventosQ.data ?? []).slice().sort((a, b) => (a.dataInicio ?? '').localeCompare(b.dataInicio ?? ''));
  }, [eventosQ.data]);
  // Lista compilada de feriados do mês corrente, em ordem de data
  const feriadosDoMes = useMemo(() => {
    const ano = cursor.getFullYear();
    const mes = cursor.getMonth();
    const out: { date: Date; nome: string; tipo: 'nacional' | 'estadual'; uf?: string }[] = [];
    for (let d = 1; d <= 31; d++) {
      const day = new Date(ano, mes, d);
      if (day.getMonth() !== mes) break;
      const list = feriadosNoDia(day);
      for (const f of list) {
        out.push({ date: day, nome: f.nome, tipo: f.tipo, uf: f.uf });
      }
    }
    return out;
  }, [cursor, feriadoIdx, showFeriados, ufFiltro]);
  const eventosFiltrados = useMemo(() => {
    if (tipoEventoFiltro === 'todos') return eventos;
    return eventos.filter((ev) => ev.tipoEvento === tipoEventoFiltro);
  }, [eventos, tipoEventoFiltro]);

  const ufsComEvento = useMemo(() => {
    const set = new Set<string>();
    for (const ev of eventos) { const u = ufDoEvento(ev); if (u) set.add(u); }
    return [...set].sort();
  }, [eventos]);

  // TODOS os eventos atuais e futuros — sem corte de 10 e sem restringir status
  // (ex.: evento sem status/realizado sumia da lateral). Filtros: busca, status, UF.
  const proximosEventos = useMemo(() => {
    const now = new Date(); now.setHours(0,0,0,0);
    const busca = buscaEvento.trim().toLowerCase();
    return eventosFiltrados.filter((ev) => {
      // Feriados importados como evento não são operacionais (só marcam data).
      if (ev.tipoEvento === 'feriado') return false;
      const start = parseISO(ev.dataInicio); const end = parseISO(ev.dataFim) ?? start;
      if (!end || end < now) return false;
      if (statusEventoFiltro !== 'todos' && (ev.status ?? 'Planejado') !== statusEventoFiltro) return false;
      if (ufEventoFiltro !== 'todos' && ufDoEvento(ev) !== ufEventoFiltro) return false;
      if (busca && !`${ev.nome} ${ev.local ?? ''}`.toLowerCase().includes(busca)) return false;
      return true;
    });
  }, [eventosFiltrados, buscaEvento, statusEventoFiltro, ufEventoFiltro]);

  const aberto = eventoOpen ? eventos.find((e) => e.id === eventoOpen) : null;

  // Monta grid 6 semanas × 7 dias do mês do cursor
  const grid = useMemo(() => {
    const ano = cursor.getFullYear();
    const mes = cursor.getMonth();
    const primeiroDia = new Date(ano, mes, 1);
    const inicio = new Date(primeiroDia);
    inicio.setDate(primeiroDia.getDate() - primeiroDia.getDay()); // recua até domingo
    const cells: { date: Date; outOfMonth: boolean }[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(inicio);
      d.setDate(inicio.getDate() + i);
      cells.push({ date: d, outOfMonth: d.getMonth() !== mes });
    }
    return cells;
  }, [cursor]);

  const eventosVisiveis = useMemo(() => {
    const mesAtual = cursor.getMonth();
    const anoAtual = cursor.getFullYear();
    return eventosFiltrados.filter((ev) => {
      const start = parseISO(ev.dataInicio);
      if (!start) return false;
      const end = parseISO(ev.dataFim) ?? start;
      return (
        (start.getFullYear() === anoAtual && start.getMonth() === mesAtual) ||
        (end.getFullYear() === anoAtual && end.getMonth() === mesAtual) ||
        (start < new Date(anoAtual, mesAtual + 1, 1) && end >= new Date(anoAtual, mesAtual, 1))
      );
    });
  }, [eventosFiltrados, cursor]);

  function prevMes() { setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1)); }
  function proxMes() { setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1)); }
  function hoje() { setCursor(new Date(today.getFullYear(), today.getMonth(), 1)); }

  return (
    <div>
      <PageHeader title="Calendário de Eventos" />
      <div className="px-6 py-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <button type="button" onClick={hoje} className="btn-secondary !py-1 !px-2.5 text-[11px]">
              Hoje
            </button>
            <div className="flex items-center bg-panel border border-border rounded">
              <button type="button" onClick={prevMes} className="px-2 py-1 text-muted hover:text-text">
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              <button type="button" onClick={proxMes} className="px-2 py-1 text-muted hover:text-text border-l border-border">
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
            <h2 className="text-sm font-bold capitalize">
              {MESES[cursor.getMonth()]} {cursor.getFullYear()}
              <span className="text-muted font-normal ml-2 text-[11px]">· {eventosVisiveis.length} no mês</span>
            </h2>
          </div>
          {/* Unificação 08/07: aba Execução foi absorvida pelo Painel (filtros + % + drawer) */}
          <div className="flex gap-0.5 bg-panel-2 border border-border rounded-lg p-0.5 mr-2">
            <button type="button" onClick={() => setViewMode('calendario')} className={`text-xs px-2.5 py-1 rounded ${viewMode === 'calendario' ? 'bg-blue text-white' : 'text-muted hover:text-text'}`}>Calendário</button>
            <button type="button" onClick={() => setViewMode('painel')} className={`text-xs px-2.5 py-1 rounded ${viewMode !== 'calendario' ? 'bg-blue text-white' : 'text-muted hover:text-text'}`}>Painel</button>
          </div>
          <button type="button" onClick={() => setNovoOpen(true)} className="btn-primary !py-1.5 !px-3 text-xs">
            <Plus className="w-3.5 h-3.5" /> Cadastrar evento
          </button>
        </div>

        {/* Feriados + tipo só fazem sentido na visão de calendário (decisão Renan 08/07) */}
        {viewMode === 'calendario' && (
        <div className="flex items-center gap-2 flex-wrap text-[11px]">
          <label className="inline-flex items-center gap-1.5 text-text-2 cursor-pointer">
            <input type="checkbox" checked={showFeriados} onChange={(e) => setShowFeriados(e.target.checked)}
              className="w-3 h-3 accent-yellow" />
            Mostrar feriados
          </label>
          {showFeriados && (
            <div className="flex items-center gap-1">
              {(['todos', 'ES', 'SC', 'RS', 'SP', 'PR'] as const).map((u) => (
                <button key={u} type="button" onClick={() => setUfFiltro(u)}
                  className={clsx(
                    'px-2 py-0.5 text-[10px] font-semibold rounded border transition-colors',
                    ufFiltro === u
                      ? 'bg-yellow/15 text-yellow border-yellow/40'
                      : 'bg-panel text-text-2 border-border hover:border-border-light',
                  )}>
                  {u === 'todos' ? 'Todos' : u}
                </button>
              ))}
            </div>
          )}
          <span className="w-px h-4 bg-border mx-1" />
          <span className="text-text-2">Tipo:</span>
          <div className="flex items-center gap-1">
            {(['todos', 'proprietario', 'patrocinado', 'setorial', 'interno', 'institucional'] as const).map((t) => (
              <button key={t} type="button" onClick={() => setTipoEventoFiltro(t)}
                className={clsx(
                  'px-2 py-0.5 text-[10px] font-semibold rounded border transition-colors',
                  tipoEventoFiltro === t
                    ? 'bg-blue/15 text-blue-light border-blue/40'
                    : 'bg-panel text-text-2 border-border hover:border-border-light',
                )}>
                {t === 'todos' ? 'Todos' : TIPO_EVENTO_LABELS[t]}
              </button>
            ))}
          </div>
        </div>
        )}

        {eventosQ.isLoading && <div className="card text-sm text-muted">Carregando…</div>}

        {!eventosQ.isLoading && viewMode === 'execucao' && (
          <PainelEventoPanel eventos={eventosQ.data ?? []} initialEventoId={painelEventoId} />
        )}

        {!eventosQ.isLoading && viewMode === 'painel' && (
          <PainelEventoPanel eventos={eventosQ.data ?? []} initialEventoId={painelEventoId} />
        )}

        {!eventosQ.isLoading && viewMode === 'calendario' && (
          <div className={clsx("grid grid-cols-1 gap-3", expanded ? "" : "lg:grid-cols-[1fr_1fr]") }>
            {/* === Coluna esquerda: calendário compacto === */}
            <div className="bg-panel border border-border rounded-lg overflow-hidden self-start">
              <div className="flex items-center justify-between px-3 py-1.5 border-b border-border bg-panel-2">
                <div className="text-[10px] font-bold uppercase tracking-wider text-text-2">Calendário</div>
                <button type="button" onClick={() => setExpanded((v) => !v)}
                  className="text-muted hover:text-text inline-flex items-center gap-1 text-[10px]"
                  title={expanded ? 'Reduzir' : 'Expandir calendário'}>
                  {expanded ? <Minimize2 className="w-3 h-3" /> : <Maximize2 className="w-3 h-3" />}
                  {expanded ? 'Reduzir' : 'Expandir'}
                </button>
              </div>
              <div className="grid grid-cols-7 border-b border-border bg-panel-2">
                {DIAS_SEMANA.map((d) => (
                  <div key={d} className="px-1 py-1 text-[9px] font-bold uppercase tracking-wider text-muted text-center">
                    {d}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 grid-rows-6">
                {grid.map((cell, i) => {
                  const isToday = sameDay(cell.date, today);
                  const evs = eventosFiltrados.filter((ev) => eventoNoDia(ev, cell.date));
                  const fers = feriadosNoDia(cell.date);
                  const hasNacional = fers.some((f) => f.tipo === 'nacional');
                  const hasEstadual = fers.some((f) => f.tipo !== 'nacional');
                  const strokeClass = hasNacional && hasEstadual
                    ? 'ring-2 ring-inset ring-orange/60'
                    : hasNacional
                      ? 'ring-2 ring-inset ring-red/50'
                      : hasEstadual
                        ? 'ring-2 ring-inset ring-yellow/50'
                        : '';
                  const cellHeight = expanded ? 'h-24' : 'h-16';
                  const hasClick = evs.length > 0 || fers.length > 0;
                  function onCellClick() {
                    if (fers.length > 0) { setFeriadoDay(cell.date); return; }
                    if (evs[0]) setEventoOpen(evs[0].id);
                  }
                  return (
                    <button
                      type="button"
                      key={i}
                      onClick={onCellClick}
                      disabled={!hasClick}
                      className={clsx(
                        'border-r border-b border-border p-0.5 flex flex-col items-center justify-start text-[10px] relative transition-colors',
                        cellHeight,
                        cell.outOfMonth && 'bg-panel-2/40 text-muted/60',
                        hasClick && 'hover:bg-panel-2 cursor-pointer',
                        !hasClick && 'cursor-default',
                        strokeClass,
                        (i + 1) % 7 === 0 && 'border-r-0',
                        i >= 35 && 'border-b-0',
                      )}
                      title={[
                        ...fers.map((f) => `${f.nome}${f.uf ? ' (' + f.uf + ')' : ' (Nacional)'}`),
                        ...evs.map((ev) => `${ev.nome} — ${ev.status ?? 'Planejado'}`),
                      ].join(' · ')}
                    >
                      <span className={clsx(
                        'inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] mt-0.5',
                        isToday && 'bg-blue text-white font-bold',
                      )}>
                        {cell.date.getDate()}
                      </span>
                      {evs.length > 0 && (
                        <div className="flex gap-0.5 mt-0.5">
                          {evs.slice(0, 3).map((ev, idx) => (
                            <span
                              key={idx}
                              role="button"
                              title={`${ev.nome} — ${ev.status ?? 'Planejado'} · abrir painel`}
                              onClick={(e) => { e.stopPropagation(); setPainelEventoId(ev.id); setViewMode('painel'); }}
                              className={clsx('w-2 h-2 rounded-full cursor-pointer hover:ring-2 hover:ring-blue/40', STATUS_PILL[ev.status ?? 'Planejado'])}
                            />
                          ))}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              {/* Legenda do calendário */}
              <div className="px-2 py-2 border-t border-border bg-panel-2 space-y-1 text-[9px]">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="inline-flex items-center gap-1 text-text-2">
                    <span className="inline-block w-2.5 h-2.5 rounded ring-2 ring-inset ring-red/50" />
                    Feriado nacional
                  </span>
                  <span className="inline-flex items-center gap-1 text-text-2">
                    <span className="inline-block w-2.5 h-2.5 rounded ring-2 ring-inset ring-yellow/50" />
                    Feriado estadual
                  </span>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  {STATUSES.map((s) => (
                    <span key={s} className="inline-flex items-center gap-1 text-text-2">
                      <span className={clsx('inline-block w-2 h-2 rounded-full', STATUS_PILL[s])} />
                      {s}
                    </span>
                  ))}
                </div>
                {feriadosDoMes.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-border italic text-text-2">
                    <div className="text-[9px] not-italic uppercase tracking-wider text-muted font-bold mb-1">
                      Feriados em {MESES[cursor.getMonth()].toLowerCase()}
                    </div>
                    <ul className="space-y-0.5">
                      {feriadosDoMes.map((f, i) => (
                        <li key={i} className="text-[10px]">
                          <span className="font-bold mr-1">{String(f.date.getDate()).padStart(2,'0')}</span>
                          {f.nome}
                          <span className="text-muted not-italic">
                            {f.tipo === 'nacional' ? ' · nacional' : ` · ${f.uf ?? 'estadual'}`}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* === Coluna direita: próximos eventos + pendências cliente === */}
            {!expanded && (
            <div className="grid grid-cols-1 gap-3 self-start">
              <div className="bg-panel border border-border rounded-lg overflow-hidden">
                <div className="px-3 py-2 border-b border-border bg-panel-2 flex items-center justify-between">
                  <h3 className="text-[11px] font-bold uppercase tracking-wider text-text">Próximos eventos</h3>
                  <span className="text-[10px] text-muted">{proximosEventos.length}</span>
                </div>
                <div className="px-3 py-2 border-b border-border bg-panel-2/60 space-y-1.5">
                  <input
                    className="input w-full text-xs"
                    placeholder="Buscar evento..."
                    value={buscaEvento}
                    onChange={(e) => setBuscaEvento(e.target.value)}
                  />
                  <div className="flex gap-1.5">
                    <select className="input flex-1 text-xs" value={statusEventoFiltro} onChange={(e) => setStatusEventoFiltro(e.target.value as StatusEvento | 'todos')}>
                      <option value="todos">Todo status</option>
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <select className="input flex-1 text-xs" value={ufEventoFiltro} onChange={(e) => setUfEventoFiltro(e.target.value)}>
                      <option value="todos">Toda UF</option>
                      {ufsComEvento.map((u) => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </div>
                </div>
                <div className="divide-y divide-border max-h-[480px] overflow-y-auto">
                  {proximosEventos.length === 0 && (
                    <div className="px-3 py-6 text-center text-muted text-[11px]">Nenhum evento encontrado.</div>
                  )}
                  {proximosEventos.map((ev) => (
                    <button
                      key={ev.id}
                      type="button"
                      onClick={() => setEventoOpen(ev.id)}
                      className={clsx(
                        'w-full text-left px-3 py-2 hover:bg-panel-2 transition-colors border-2',
                        ev.familia ? FAMILIA_COR[ev.familia] : 'border-border',
                      )}
                    >
                      <div className="text-[12px] font-bold text-text truncate">{ev.nome}</div>
                      <div className="flex items-center gap-2 mt-0.5 text-[10px] text-muted">
                        <span>{fmt(ev.dataInicio)}</span>
                        {ev.local && <span>· {ev.local}</span>}
                        {ev.status && (
                          <span className={clsx('inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold border ml-auto', STATUS_COR[ev.status])}>
                            {ev.status}
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

            </div>
            )}
          </div>
        )}
      </div>

      {aberto && <EventoModal evento={aberto} onClose={() => setEventoOpen(null)} />}
      {feriadoDay && (
        <FeriadoModal
          day={feriadoDay}
          feriados={feriadosNoDia(feriadoDay)}
          onClose={() => setFeriadoDay(null)}
        />
      )}
      {novoOpen && <NovoEventoModal onClose={() => setNovoOpen(false)} />}
    </div>
  );
}

function EventoModal({ evento, onClose }: { evento: Evento; onClose: () => void }) {
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; cardsGerados: number; cardsExistentes?: number; familia?: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [familias, setFamilias] = useState<string[] | null>(null);
  const [familiaSel, setFamiliaSel] = useState('');

  async function confirmarEvento() {
    setConfirming(true);
    setErr(null);
    try {
      const res = await api.eventos.confirmar(evento.id, familiaSel || undefined);
      setResult({ ok: true, cardsGerados: res.cardsGerados ?? 0, cardsExistentes: res.cardsExistentes, familia: res.familia });
      setFamilias(null);
      qc.invalidateQueries({ queryKey: ['eventos'] });
      qc.invalidateQueries({ queryKey: ['demandas'] });
    } catch (e) {
      const body = e instanceof ApiError ? (e.body as { message?: string; error?: string; familias?: string[] }) : null;
      if (body?.error === 'familia_ambigua') setFamilias(body.familias ?? []);
      const msg = body?.message ?? (e instanceof Error ? e.message : String(e));
      setErr(msg);
    } finally {
      setConfirming(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-6" tabIndex={-1} ref={(el)=>{if(el&&el.dataset.esc!=='1'){el.dataset.esc='1';el.focus();}}} onKeyDown={(e)=>{if(e.key==='Escape')onClose();}}>
      <div onClick={(e) => e.stopPropagation()}
        className={clsx(
          'bg-panel border border-border border-2 rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto',
          evento.familia ? FAMILIA_COR[evento.familia] : 'border-border',
        )}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-border sticky top-0 bg-panel z-10">
          <div>
            <h2 className="text-sm font-bold">{evento.nome}</h2>
            {evento.familia && <p className="text-[10px] text-muted mt-0.5">{evento.familia}</p>}
          </div>
          <button type="button" onClick={onClose} className="text-muted hover:text-text"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3 text-xs">
          <div className="grid grid-cols-2 gap-2">
            {/* Evento normalmente dura 1 dia — mostra data única; intervalo só se dataFim divergir */}
            {(!evento.dataFim || evento.dataFim === evento.dataInicio) ? (
              <Info label="Data do evento" value={fmt(evento.dataInicio)} />
            ) : (
              <Info label="Data do evento" value={`${fmt(evento.dataInicio)} → ${fmt(evento.dataFim)}`} />
            )}
            <Info label="Local" value={evento.local ?? '—'} />
            <Info label="Status">
              {evento.status && <span className={clsx('inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border', STATUS_COR[evento.status])}>{evento.status}</span>}
            </Info>
          </div>
          {evento.descricao && (
            <div>
              <div className="text-[9px] uppercase tracking-wider text-muted mb-0.5">Descrição</div>
              <p className="text-text leading-relaxed">{evento.descricao}</p>
            </div>
          )}
          {evento.inscricoesAbertas && (
            <div className="bg-green/10 border border-green/30 rounded-lg p-3 text-green text-xs">
              ✓ Inscrições abertas — clientes vão receber notificação no portal.
            </div>
          )}

          {/* Confirmar evento → gera cards Tipo B */}
          {evento.status === 'Planejado' && !result && (
            <div className="border border-blue/30 bg-blue/5 rounded-lg p-3 space-y-2">
              <p className="text-text font-semibold text-[11px]">
                Confirmar este evento? Isso vai:
              </p>
              <ul className="text-[10px] text-text-2 space-y-0.5 ml-3 list-disc">
                <li>Mudar status para <strong>Confirmado</strong></li>
                <li>Gerar <strong>1 card-mãe por time</strong> com o checklist da família (checks + INFOs)</li>
                <li>Gerar os <strong>cards de execução</strong> com prazos D-N (ex.: Backdrop D-20)</li>
                <li>Criar o card <strong>Enxoval de Ativações (MAC)</strong></li>
              </ul>
              {familias && (
                <label className="block text-[10px] text-text-2">
                  Família do template (não resolvida automaticamente):
                  <select className="input w-full text-xs mt-1" value={familiaSel} onChange={(e) => setFamiliaSel(e.target.value)}>
                    <option value="">— escolher —</option>
                    {familias.map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                </label>
              )}
              {err && (
                <div className="text-xs text-red bg-red/10 border border-red/30 rounded px-2 py-1.5 flex items-start gap-2">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> <span>{err}</span>
                </div>
              )}
              <button type="button" onClick={confirmarEvento} disabled={confirming}
                className="btn-primary !py-1.5 !px-4 text-xs w-full disabled:opacity-50">
                {confirming ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Confirmando…</> : 'Confirmar evento'}
              </button>
            </div>
          )}

          {result && (
            <div className="bg-green/10 border border-green/30 rounded-lg p-3 text-green text-xs space-y-1">
              <p className="font-bold">Evento confirmado com sucesso!</p>
              <p>
                {result.cardsGerados} cards gerados pelo template{result.familia ? ` (${result.familia})` : ''}
                {result.cardsExistentes ? ` · ${result.cardsExistentes} já existiam` : ''}.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Info({ label, value, children }: { label: string; value?: string; children?: React.ReactNode }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-muted mb-0.5">{label}</div>
      {children ?? <div className="text-text font-semibold">{value}</div>}
    </div>
  );
}

function NovoEventoModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [nome, setNome] = useState('');
  const [familia, setFamilia] = useState<FamiliaEvento>('Conecta Folha Business');
  const [status, setStatus] = useState<StatusEvento>('Planejado');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [multiDia, setMultiDia] = useState(false);
  const [local, setLocal] = useState('');
  const [descricao, setDescricao] = useState('');
  const [inscricoesAbertas, setInscricoesAbertas] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (body: Partial<Evento>) => api.eventos.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['eventos'] });
      onClose();
    },
    onError: (e) => {
      const msg = e instanceof ApiError ? (e.body as { message?: string })?.message ?? e.message : String(e);
      setErr(msg);
    },
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!nome.trim()) { setErr('Nome obrigatório'); return; }
    setErr(null);
    create.mutate({
      nome: nome.trim(),
      familia,
      status,
      dataInicio: dataInicio || undefined,
      // Evento dura 1 dia por padrão: dataFim = data de execução (multi-dia é exceção explícita)
      dataFim: (multiDia && dataFim) || dataInicio || undefined,
      local: local || undefined,
      descricao: descricao || undefined,
      inscricoesAbertas,
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-6" tabIndex={-1} ref={(el)=>{if(el&&el.dataset.esc!=='1'){el.dataset.esc='1';el.focus();}}} onKeyDown={(e)=>{if(e.key==='Escape')onClose();}}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-panel border border-border rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border sticky top-0 bg-panel z-10">
          <h2 className="text-sm font-bold">Cadastrar evento</h2>
          <button type="button" onClick={onClose} className="text-muted hover:text-text"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3 text-xs">
          <div>
            <Label>Nome <span className="text-red">*</span></Label>
            <input autoFocus className="input w-full text-sm" value={nome} onChange={(e) => setNome(e.target.value)} placeholder="ex.: Conecta Summit 2026" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Família</Label>
              <select className="input w-full text-sm" value={familia} onChange={(e) => setFamilia(e.target.value as FamiliaEvento)}>
                {FAMILIAS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div>
              <Label>Status</Label>
              <select className="input w-full text-sm" value={status} onChange={(e) => setStatus(e.target.value as StatusEvento)}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Data do evento</Label>
              <input type="date" className="input w-full text-sm" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
            </div>
            {multiDia && (
              <div>
                <Label>Último dia</Label>
                <input type="date" className="input w-full text-sm" min={dataInicio || undefined} value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
              </div>
            )}
          </div>
          <label className="flex items-center gap-2 text-text-2 text-[11px]">
            <input type="checkbox" checked={multiDia} onChange={(e) => { setMultiDia(e.target.checked); if (!e.target.checked) setDataFim(''); }} className="w-3 h-3 accent-blue-light" />
            Evento de múltiplos dias (exceção — ex.: fórum de 2 dias)
          </label>
          <div>
            <Label>Local</Label>
            <input className="input w-full text-sm" value={local} onChange={(e) => setLocal(e.target.value)} placeholder="ex.: Vitória, ES" />
          </div>
          <div>
            <Label>Descrição</Label>
            <textarea rows={3} className="input w-full text-sm resize-none" value={descricao} onChange={(e) => setDescricao(e.target.value)} />
          </div>
          <label className="flex items-center gap-2 text-text-2">
            <input type="checkbox" checked={inscricoesAbertas} onChange={(e) => setInscricoesAbertas(e.target.checked)} className="w-3 h-3 accent-blue-light" />
            Inscrições abertas pros clientes
          </label>
          {err && (
            <div className="text-xs text-red bg-red/10 border border-red/30 rounded-lg px-2.5 py-2 flex items-start gap-2">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> <span>{err}</span>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-border flex items-center justify-end gap-2 sticky bottom-0 bg-panel">
          <button type="button" onClick={onClose} className="btn-secondary !py-1.5 !px-3 text-xs">Cancelar</button>
          <button type="submit" disabled={create.isPending} className="btn-primary !py-1.5 !px-3 text-xs disabled:opacity-50">
            {create.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Salvar evento
          </button>
        </div>
      </form>
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return <label className="block text-[10px] font-semibold text-text-2 uppercase tracking-wide mb-1">{children}</label>;
}


function FeriadoModal({ day, feriados, onClose }: { day: Date; feriados: Feriado[]; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-6" tabIndex={-1} ref={(el)=>{if(el&&el.dataset.esc!=='1'){el.dataset.esc='1';el.focus();}}} onKeyDown={(e)=>{if(e.key==='Escape')onClose();}}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-panel border border-border rounded-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-panel-2">
          <div>
            <h2 className="text-sm font-bold">
              {day.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
            </h2>
            <p className="text-[10px] text-muted mt-0.5">
              {feriados.length === 1 ? '1 feriado' : `${feriados.length} feriados`} neste dia
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-muted hover:text-text">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          {feriados.map((f, i) => (
            <div key={i} className={clsx(
              'border-2 pl-3 py-1',
              f.tipo === 'nacional' ? 'border-red/40' : 'border-orange/40',
            )}>
              <div className="text-sm font-bold text-text">{f.nome}</div>
              <div className="text-[11px] text-muted mt-0.5">
                {f.tipo === 'nacional' ? 'Feriado nacional' : `Feriado estadual · ${f.uf ?? '—'}`}
              </div>
            </div>
          ))}
          {feriados.length === 0 && (
            <div className="text-sm text-muted text-center py-3">Sem feriados neste dia.</div>
          )}
        </div>
      </div>
    </div>
  );
}


// Polos reconhecidos: 27 UFs brasileiras + polos internacionais (NY, PT/Lisboa).
const UFS_BR = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'];
const POLOS_INTL = ['NY','PT'];
const POLOS = [...UFS_BR, ...POLOS_INTL];

/** Polo (UF) do evento: usa o campo `uf`; fallback: deriva do texto `local`. */
function ufDoEvento(ev: Evento): string | null {
  if (ev.uf) return ev.uf.toUpperCase();
  const tokens = (ev.local ?? '').toUpperCase().match(/\b[A-Z]{2}\b/g);
  if (tokens) for (const t of tokens) if (POLOS.includes(t)) return t;
  return null;
}

// NOTE (09/07/2026): EventosExecucaoPanel (aba Execução, Sprint 9) foi removido —
// a Execução foi unificada no PainelEventoPanel (components/PainelEvento.tsx).
// A rota /eventos/:id/execucao ainda é usada pelo portal (EventoDetalhePortal).
